import { observable, action } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";

/**
 * Store for the Agents feature (user-configured workflow automations).
 * Talks to the internal /cm/agents API. Mirrors the app's other MobX stores:
 * observable state + async actions using makeApi(config).
 */
/** Why a team with this usage ({enabled, eligible, used}) can't have another agent. */
export const agentLimitMessage = usage =>
  usage && usage.eligible > 0
    ? `Your team has reached its limit of ${usage.eligible} ${usage.eligible === 1 ? "agent" : "agents"}. Delete an agent or contact us to add more.`
    : "Your team's plan doesn't include agents yet. Contact us to add them.";

class AgentsData {
  @observable agents = [];
  @observable agentsLoading = false;
  @observable currentAgent = null;
  @observable runs = [];
  @observable lastRun = null;
  @observable runRecords = {};
  @observable runRecordsLoading = {};
  @observable icpPreview = null;
  @observable busy = false;
  @observable testing = false; // a dry run in progress (separate from busy, which Run now / approvals use)
  @observable error = "";
  // The team's agent access: {enabled, eligible, used}.
  @observable usage = null;
  @observable approvals = [];
  @observable approvalsLoading = false;
  @observable pendingApprovalCount = 0;
  @observable folders = [];
  // A draft agent spec composed by AI from a description, handed to the builder to prefill.
  @observable generatedDraft = null;
  // The words the user actually typed to get that draft. Shown on the review page so they can
  // check the flow against what they asked for; deliberately never saved on the agent — once it
  // exists, the agent's own summary is the description of it.
  @observable generatedPrompt = "";
  // Source pickers for the flow builder.
  @observable contactLists = [];
  @observable savedSearches = [];
  // CRMs an Export to CRM step can use: [{value, label, connected}].
  @observable crms = [];
  @observable triggerTypes = [];
  @observable contactStages = [];
  @observable contactFields = [];
  // Slack channels an agent's notifications can be pointed at. Empty when Slack isn't connected.
  @observable slackChannels = [];
  @observable slackChannelsLoaded = false;
  @observable sequencesLite = [];
  @observable accountLists = [];
  @observable teamMembers = []; // for "Get a report" → one team member
  @observable teamGroups = [];  // …or a group
  // Field vocabulary for the condition editor (branch.if / filter.condition).
  @observable conditionFields = [];

  fill(url, params = {}) {
    let filled = url;
    Object.keys(params).forEach(k => {
      filled = filled.replace(`<<${k}>>`, encodeURIComponent(params[k]));
    });
    return filled;
  }

  ok(res) {
    return res && res.status === 200;
  }

  // A create that failed: say why (e.g. the team's agent limit), refreshing usage when it's the limit.
  createFailed(res) {
    const body = (res && res.response && res.response.data) || {};
    const msg = body.error || "Could not create the agent.";
    if (body.code === "agent_limit_reached") this.fetchUsage();
    toasterState.setToastMsg(msg, "failure");
    return msg;
  }

  // Before starting a new agent (Hire, a template, Build it): false — with the reason shown — when the
  // team has no room for another, so the user isn't taken through setup only to be refused on create.
  async canHireAgent() {
    const usage = await this.fetchUsage();
    if (usage && usage.enabled && usage.used >= usage.eligible) {
      toasterState.setToastMsg(agentLimitMessage(usage), "failure");
      return false;
    }
    return true;
  }

  @action.bound
  async fetchUsage() {
    const res = await makeApi({ url: URLS.agentUsage, method: "GET" });
    if (this.ok(res) && res.data) this.usage = res.data;
    return this.usage;
  }

  @action.bound
  async fetchAgents() {
    this.agentsLoading = true;
    const res = await makeApi({ url: URLS.agentsList, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.agents = res.data;
    }
    this.agentsLoading = false;
    return this.agents;
  }

  @action.bound
  async fetchTemplates() {
    const res = await makeApi({ url: URLS.agentTemplates, method: "GET" });
    return this.ok(res) ? res.data : [];
  }

  @action.bound
  async getAgent(id) {
    const res = await makeApi({ url: this.fill(URLS.agentGet, { id }), method: "GET" });
    // Clear on failure (missing, or not this user's) so a page never shows a previously
    // opened agent under a different id.
    this.currentAgent = this.ok(res) ? res.data : null;
    return this.currentAgent;
  }

  @action.bound
  async createDailyIcpProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({
      url: URLS.agentCreateDailyIcpProspector,
      method: "POST",
      data: payload
    });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  @action.bound
  async fetchContactLists() {
    const res = await makeApi({ url: URLS.agentPickContactLists, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.contactLists = res.data;
    return this.contactLists;
  }

  // Creates a contact list (same endpoint as Contacts → New list) and adds it to the pickers.
  // Resolves to { list } or { error } with a message to show under the name field.
  @action.bound
  async createContactList(name) {
    const res = await makeApi({ url: URLS.createContactList, method: "POST", data: { listName: name } });
    const lr = res?.data?.listResponse;
    if (lr?.statusCode === 409) return { error: "A list with this name already exists." };
    const list = lr?.success ? lr.list?.[0] : null;
    if (!list?.id) return { error: "Couldn’t create the list. Please try again." };
    this.contactLists = [{ id: list.id, name: list.name, count: 0 }, ...this.contactLists];
    return { list };
  }

  @action.bound
  async fetchCrms() {
    const res = await makeApi({ url: URLS.agentPickCrms, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.crms = res.data;
    return this.crms;
  }

  // The events an agent can react to, with the filters each one offers (stage changed -> stages).
  @action.bound
  async fetchTriggerTypes() {
    if (this.triggerTypes.length) return this.triggerTypes;
    const res = await makeApi({ url: URLS.agentTriggerTypes, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.triggerTypes = res.data;
    return this.triggerTypes;
  }

  @action.bound
  async fetchContactStages() {
    const res = await makeApi({ url: URLS.agentPickContactStages, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.contactStages = res.data;
    return this.contactStages;
  }

  @action.bound
  async fetchContactFields() {
    const res = await makeApi({ url: URLS.agentPickContactFields, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.contactFields = res.data;
    return this.contactFields;
  }

  // The team's Slack channels. An empty list means Slack isn't connected, which is why the
  // "loaded" flag is separate — the UI has to tell "no Slack" from "not asked yet".
  @action.bound
  async fetchSlackChannels() {
    const res = await makeApi({ url: URLS.agentPickSlackChannels, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.slackChannels = res.data;
    this.slackChannelsLoaded = true;
    return this.slackChannels;
  }

  // Sequences as {value, label}, for the "only in these sequences" trigger filter.
  @action.bound
  async fetchSequencesLite() {
    const res = await makeApi({ url: URLS.sequencesLite, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.sequencesLite = res.data.map(s => ({ value: s.id, label: s.name }));
    }
    return this.sequencesLite;
  }

  @action.bound
  async fetchSavedSearches() {
    const res = await makeApi({ url: URLS.agentPickSavedSearches, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.savedSearches = res.data;
    return this.savedSearches;
  }

  @action.bound
  async fetchTeamMembers() {
    const res = await makeApi({ url: URLS.agentPickTeamMembers, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.teamMembers = res.data;
    return this.teamMembers;
  }

  @action.bound
  async fetchTeamGroups() {
    const res = await makeApi({ url: URLS.agentPickTeamGroups, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.teamGroups = res.data;
    return this.teamGroups;
  }

  @action.bound
  async fetchAccountLists() {
    const res = await makeApi({ url: URLS.agentPickAccountLists, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.accountLists = res.data;
    return this.accountLists;
  }

  async createAccountProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateAccountProspector, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  @action.bound
  async fetchConditionFields() {
    const res = await makeApi({ url: URLS.agentConditionFields, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.conditionFields = res.data;
    return this.conditionFields;
  }

  @action.bound
  async fetchStepTypes() {
    const res = await makeApi({ url: URLS.agentStepTypes, method: "GET" });
    return this.ok(res) && Array.isArray(res.data) ? res.data : [];
  }

  @action.bound
  async createAgent(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreate, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  @action.bound
  async generateAgent(prompt) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentGenerate, method: "POST", data: { prompt } });
    this.busy = false;
    // Success: a 200 carrying the draft agent (which never has an `error` field).
    if (this.ok(res) && res.data && !res.data.error) {
      this.generatedDraft = res.data;
      this.generatedPrompt = prompt || "";
      return res.data;
    }
    // Failure: surface the server's friendly reason. makeApi returns the axios error on
    // non-2xx, so the body lives under res.response.data for 4xx/5xx.
    const serverMsg =
      (res && res.data && res.data.error) ||
      (res && res.response && res.response.data && res.response.data.error);
    this.error = serverMsg || "Couldn't generate that agent. Try rephrasing your description.";
    return null;
  }

  @action.bound
  // Take the staged draft only if it's for the given template (so another screen's draft is left alone).
  consumeTemplateDraft(templateKey) {
    if (!this.generatedDraft || this.generatedDraft.templateKey !== templateKey) return null;
    return this.consumeGeneratedDraft();
  }

  consumeGeneratedDraft() {
    const d = this.generatedDraft;
    this.generatedDraft = null;
    // Keep the prompt: the review page renders after this runs, and it's cleared on leaving.
    return d;
  }

  // The review page is done with the typed prompt — drop it so it can't leak into the next draft.
  @action.bound
  clearGeneratedPrompt() {
    this.generatedPrompt = "";
  }

  // Stage a composed template as the builder draft (for "Customize steps" → canvas).
  @action.bound
  stageDraft(draft) {
    this.generatedDraft = draft;
  }

  // Compose (but don't create) a template's flow from the given inputs, for pre-create editing.
  @action.bound
  async composeTemplate(key, payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: this.fill(URLS.agentComposeTemplate, { key }), method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res) && res.data) return res.data;
    this.error = "Could not open this template for editing.";
    return null;
  }

  @action.bound
  async createEventAgent(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({
      url: URLS.agentCreateEventAgent,
      method: "POST",
      data: payload
    });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createListProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateListProspector, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createCompanyProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateCompanyProspector, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createLookalikeProspector(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateLookalikeProspector, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createLinkedinImport(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateLinkedinImport, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  async createReplyRouter(payload) {
    this.busy = true;
    this.error = "";
    const res = await makeApi({ url: URLS.agentCreateReplyRouter, method: "POST", data: payload });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      return res.data;
    }
    this.error = this.createFailed(res);
    return null;
  }

  @action.bound
  async previewIcp(id) {
    this.busy = true;
    const res = await makeApi({ url: this.fill(URLS.agentIcpPreview, { id }), method: "GET" });
    this.busy = false;
    if (this.ok(res)) {
      this.icpPreview = res.data;
      return res.data;
    }
    return null;
  }

  // Describe + preview List Builder filter state for a drafted search step (after refining it in List Builder).
  async previewListBuilderSearch(state, companies) {
    const res = await makeApi({ url: URLS.agentSearchPreview, method: "POST", data: { state, companies: !!companies } });
    return this.ok(res) && res.data && res.data.state ? res.data : null;
  }

  // Use a List Builder saved search as the agent's ICP (this approves the ICP).
  @action.bound
  async setIcpSavedSearch(id, savedSearchId) {
    this.busy = true;
    const res = await makeApi({
      url: this.fill(URLS.agentIcpSavedSearch, { id }),
      method: "POST",
      data: { savedSearchId }
    });
    this.busy = false;
    if (this.ok(res) && res.data && res.data.id) {
      if (this.currentAgent && this.currentAgent.id === id) this.currentAgent = res.data;
      return res.data;
    }
    return null;
  }

  @action.bound
  async setStatus(id, status) {
    const res = await makeApi({
      url: this.fill(URLS.agentSetStatus, { id, status }),
      method: "POST"
    });
    if (this.ok(res)) {
      this.currentAgent = res.data;
      this.agents = this.agents.map(a => (a.id === id ? res.data : a));
      return res.data;
    }
    return null;
  }

  @action.bound
  async deleteAgent(id) {
    this.busy = true;
    const res = await makeApi({ url: this.fill(URLS.agentDelete, { id }), method: "DELETE" });
    this.busy = false;
    if (this.ok(res)) {
      this.agents = this.agents.filter(a => a.id !== id);
      if (this.currentAgent && this.currentAgent.id === id) this.currentAgent = null;
      return true;
    }
    return false;
  }

  @action.bound
  async updateAgent(id, changes) {
    this.busy = true;
    const res = await makeApi({ url: this.fill(URLS.agentUpdate, { id }), method: "PUT", data: changes });
    this.busy = false;
    if (this.ok(res)) {
      this.currentAgent = res.data;
      this.agents = this.agents.map(a => (a.id === id ? res.data : a));
      return res.data;
    }
    return null;
  }

  @action.bound
  async fetchFolders() {
    const res = await makeApi({ url: URLS.agentFolders, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) this.folders = res.data;
    return this.folders;
  }

  @action.bound
  async createFolder(name) {
    const res = await makeApi({ url: URLS.agentFolders, method: "POST", data: { name } });
    if (this.ok(res) && Array.isArray(res.data)) this.folders = res.data;
    return this.folders;
  }

  @action.bound
  async renameFolder(oldName, newName) {
    const res = await makeApi({ url: URLS.agentFolders, method: "PUT", data: { oldName, newName } });
    if (this.ok(res) && Array.isArray(res.data)) { this.folders = res.data; await this.fetchAgents(); }
    return this.folders;
  }

  @action.bound
  async deleteFolder(name) {
    const res = await makeApi({ url: URLS.agentFolderDelete, method: "POST", data: { name } });
    if (this.ok(res) && Array.isArray(res.data)) { this.folders = res.data; await this.fetchAgents(); }
    return this.folders;
  }

  @action.bound
  async setFolder(id, folder) {
    const res = await makeApi({
      url: this.fill(URLS.agentSetFolder, { id, folder: folder || "" }),
      method: "POST"
    });
    if (this.ok(res)) {
      this.currentAgent = res.data;
      this.agents = this.agents.map(a => (a.id === id ? res.data : a));
      return res.data;
    }
    return null;
  }

  @action.bound
  async testRun(id) {
    this.testing = true;
    const res = await makeApi({ url: this.fill(URLS.agentTest, { id }), method: "POST" });
    this.testing = false;
    if (this.ok(res)) {
      this.lastRun = res.data;
      return res.data;
    }
    return null;
  }

  isTerminalRun(status) {
    // SLEEPING = parked on a wait step or a background job — settled until the wait scanner resumes it.
    return ["SUCCEEDED", "FAILED", "PARTIAL", "CANCELLED", "WAITING_APPROVAL", "SLEEPING"].includes(status);
  }

  @action.bound
  async runNow(id, inputs) {
    this.busy = true;
    const res = await makeApi({ url: this.fill(URLS.agentRun, { id }), method: "POST", data: inputs || {} });
    if (!this.ok(res) || !res.data) {
      this.busy = false;
      return null;
    }
    // Runs are async now — the POST returns a QUEUED run. Poll until it's terminal,
    // refreshing the runs list each cycle so the UI shows live progress.
    const runId = res.data.id;
    this.lastRun = res.data;
    const deadline = Date.now() + 5 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 3000));
      const runs = await this.fetchRuns(id);
      const current = (runs || []).find(x => x.id === runId);
      if (current) {
        this.lastRun = current;
        if (this.isTerminalRun(current.status)) break;
      }
    }
    this.busy = false;
    return this.lastRun;
  }

  @action.bound
  async fetchApprovals() {
    this.approvalsLoading = true;
    const res = await makeApi({ url: URLS.agentApprovals, method: "GET" });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.approvals = res.data;
      this.pendingApprovalCount = res.data.length;
    }
    this.approvalsLoading = false;
    return this.approvals;
  }

  @action.bound
  async fetchApprovalCount() {
    const res = await makeApi({ url: URLS.agentApprovalsCount, method: "GET" });
    if (this.ok(res) && res.data) {
      this.pendingApprovalCount = res.data.count || 0;
    }
    return this.pendingApprovalCount;
  }

  @action.bound
  async approveBatch(id, contactIds) {
    this.busy = true;
    const res = await makeApi({
      url: this.fill(URLS.agentApprovalApprove, { id }),
      method: "POST",
      data: contactIds ? { contactIds } : {}
    });
    this.busy = false;
    if (this.ok(res)) {
      this.approvals = this.approvals.filter(a => a.id !== id);
      this.pendingApprovalCount = this.approvals.length;
      return res.data;
    }
    return null;
  }

  @action.bound
  async rejectBatch(id) {
    this.busy = true;
    const res = await makeApi({
      url: this.fill(URLS.agentApprovalReject, { id }),
      method: "POST"
    });
    this.busy = false;
    if (this.ok(res)) {
      this.approvals = this.approvals.filter(a => a.id !== id);
      this.pendingApprovalCount = this.approvals.length;
      return res.data;
    }
    return null;
  }

  @action.bound
  async fetchRuns(id, limit = 20) {
    const res = await makeApi({
      url: `${this.fill(URLS.agentRuns, { id })}?limit=${limit}`,
      method: "GET"
    });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.runs = res.data;
    }
    return this.runs;
  }

  @action.bound
  async fetchRunRecords(id, runId) {
    this.runRecordsLoading = { ...this.runRecordsLoading, [runId]: true };
    const res = await makeApi({
      url: this.fill(URLS.agentRunRecords, { id, runId }),
      method: "GET"
    });
    if (this.ok(res) && Array.isArray(res.data)) {
      this.runRecords = { ...this.runRecords, [runId]: res.data };
    }
    this.runRecordsLoading = { ...this.runRecordsLoading, [runId]: false };
    return this.runRecords[runId] || [];
  }
}

export const agentsData = new AgentsData();
export default agentsData;
